import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { setPageMeta } from "../utils/setPageMeta";

const LAST_UPDATED = "2026-03-18";

export default function VisualMotivatorPrivacy() {
  const [scrollY, setScrollY] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    setPageMeta({
      title: "Privacy Policy — Visual Motivator | LeadingNG",
      description:
        "Privacy Policy for the Visual Motivator app by LeadingNG: data stored on your device, notifications, analytics, and your rights. Last updated with the policy date on this page.",
      canonicalPath: "/app/visual-motivator/privacy",
      keywords:
        "Visual Motivator privacy, LeadingNG privacy policy, app privacy, mobile app data",
    });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      const windowHeight = window.innerHeight;
      const documentHeight =
        document.documentElement.scrollHeight - windowHeight;
      setScrollProgress(
        documentHeight > 0 ? (window.scrollY / documentHeight) * 100 : 0,
      );
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <div
        className="scroll-progress-bar"
        style={{ width: `${scrollProgress}%` }}
        aria-hidden="true"
      />

      <section className="page-hero page-hero-privacy">
        <div
          className="hero-bg"
          style={{ transform: `translateY(${scrollY * 0.5}px)` }}
        />
        <div className="container text-center">
          <span className="page-hero-label">Visual Motivator</span>
          <h1 className="page-title">Privacy Policy</h1>
          <p className="page-intro page-intro-muted">
            Last updated: {LAST_UPDATED}
          </p>
        </div>
      </section>

      <section className="section privacy-section">
        <div className="container">
          <div className="privacy-content">
            <p className="privacy-intro">
              This Privacy Policy applies to the Visual Motivator mobile
              application (“App”) for iOS and Android, published by LeadingNG.
              The App does not require an account or sign-in; all data stays on
              your device.
            </p>
            <p className="privacy-local-only">
              <strong>All data is stored locally on your mobile device.</strong>{" "}
              Nothing is sent to the internet, to our servers, or to any third
              party. We do not collect, transmit, or sell your personal
              data—your information never leaves your phone.
            </p>

            <h2 id="data-accessed-collected">Data we access and collect</h2>
            <p>
              The App accesses and/or stores the following data{" "}
              <strong>only on your device</strong>. Nothing leaves your mobile
              device.
            </p>
            <ul>
              <li>
                <strong>Photos and media:</strong> Images and videos you add to
                your visual board (from your photo library or camera). Used only
                to display your board.
              </li>
              <li>
                <strong>Reminders and notifications:</strong> Notification
                settings and schedules you configure. Used only to show local
                reminders on your device.
              </li>
              <li>
                <strong>Usage and streak data:</strong> Visit dates, streak
                counts, and similar in-app progress. Used only within the App to
                show your activity.
              </li>
              <li>
                <strong>User-generated content:</strong> Optional progress
                notes, captions, or other text you enter. Stored only in the
                App’s local database.
              </li>
              <li>
                <strong>App preferences:</strong> Settings such as lock
                preference and display options. Stored only on your device.
              </li>
            </ul>
            <p>
              We do not upload, receive, or have access to any of this data. It
              never leaves your mobile device unless you share it yourself (e.g.
              by exporting or backing up your phone).
            </p>

            <h2 id="data-sharing">Data sharing and sale</h2>
            <p>
              We do <strong>not</strong> sell, rent, or share your personal data
              with third parties. We do not use your data for advertising or
              marketing. All data remains on your mobile device and is not
              transmitted elsewhere; we therefore have no data to share or sell.
            </p>

            <h2 id="health-data">Health Data</h2>
            <p>
              The App does <strong>not</strong> integrate with Apple Health,
              Google Fit, or any other health or fitness platform. We do not
              read, write, or collect health data from such services.
            </p>
            <p>
              Any wellness-related data in the App (such as streak counts or
              visit dates) is generated and stored only on your device for
              in-app features. It is not shared with us or any third party and
              is not linked to external health APIs.
            </p>

            <h2 id="data-we-store">Data we store (technical)</h2>
            <p>
              All of the data described above is stored{" "}
              <strong>only on your mobile device</strong> using local storage
              (SQLite). Nothing is uploaded to any server or sent over the
              internet. We do not have access to your data.
            </p>

            <h2 id="notifications">Notifications</h2>
            <p>
              The App uses local notifications (via your device’s notification
              system) to show reminders you set—for example a daily “check your
              board” reminder and optional per-vision reminders (custom title,
              time, and days per board item). Notification content and timing
              are determined only by your settings. We do not send push
              notifications from a server or track how you interact with them.
            </p>

            <h2 id="biometrics">Biometric authentication</h2>
            <p>
              If you enable the optional lock feature, the App may use your
              device’s biometric authentication (e.g. Face ID or fingerprint) to
              unlock the App. Biometric data is handled entirely by your
              device’s operating system; we do not receive or store any
              biometric information.
            </p>

            <h2 id="photos-and-media">Photos and media</h2>
            <p>
              The App may request access to your photo library or camera so you
              can add images and videos to your visual board. Media you select
              is stored only on your device within the App’s local database. We
              do not transmit or copy it elsewhere.
            </p>

            <h2 id="location-and-other">Location and other sensitive data</h2>
            <p>
              The App does <strong>not</strong> collect or use your location. We
              do not access contacts, calendar, or other sensitive data except
              as already described (photos/media for your board, optional
              biometrics for the lock, and local notifications).
            </p>

            <h2 id="security">Security</h2>
            <p>
              Your data is stored only on your device and is never transmitted
              over the internet or to our systems. We do not have access to it.
              The App does not use encryption for data in transit because no
              data is sent. Device backup (e.g. iCloud or Google) may include
              App data according to your backup settings; that is controlled by
              your device and OS, not by us.
            </p>

            <h2 id="third-parties">Third parties</h2>
            <p>
              The App is built with Expo and may use standard Expo modules (e.g.
              for notifications, storage, and device features). We do not
              integrate analytics, advertising, or third-party tracking. For
              distribution, the App may be distributed via app stores (e.g.
              Google Play, Apple App Store), which have their own privacy
              policies.
            </p>

            <h2 id="retention-deletion">Data retention and deletion</h2>
            <p>
              <strong>How long we store your data:</strong> Because all data
              stays on your device, we do not retain it on our systems. Your
              data remains on your device until you delete it or remove the App.
            </p>
            <p>
              <strong>How to delete your data:</strong> You can have all App
              data removed in the following ways:
            </p>
            <ul>
              <li>
                <strong>Uninstall the App:</strong> Removing Visual Motivator
                from your device deletes all data stored by the App (boards,
                images, reminders, streaks, notes). This is permanent and we
                have no copy of it.
              </li>
              <li>
                <strong>Clear app data (Android):</strong> In your device
                Settings → Apps → Visual Motivator → Storage, use “Clear data”
                or “Clear storage” to delete all App data without uninstalling.
              </li>
              <li>
                <strong>Delete app data (iOS):</strong> Uninstalling the App
                removes all of its data. There is no separate in-OS “clear data”
                option; reinstalling gives you a fresh start.
              </li>
            </ul>
            <p>
              We do not transfer your data to any other country; it never leaves
              your device. After deletion, we have no record of your data (we
              never received it). If you need help or confirmation, contact us
              at the address below.
            </p>

            <h2 id="your-rights">Your rights (access, correction, deletion)</h2>
            <p>
              Because all data is stored only on your device, you have full
              control: you can view and edit it within the App, and you can
              delete all of it at any time using the options in the “Data
              retention and deletion” section above. We do not hold a copy of
              your data, so there is nothing for us to provide or correct on our
              side; all access and deletion is done on your device.
            </p>

            <h2 id="children">Children</h2>
            <p>
              The App is not directed at children under 13 (or equivalent
              minimum age in your region). We do not knowingly collect personal
              data from children. If you believe a child has provided data
              through the App, please contact us; the data would be stored only
              on that device and can be removed by uninstalling the App.
            </p>

            <h2 id="changes">Changes to this policy</h2>
            <p>
              We may update this Privacy Policy from time to time. The “Last
              updated” date at the top will be revised when we do. Continued use
              of the App after changes constitutes acceptance of the updated
              policy.
            </p>

            <h2 id="contact">Contact</h2>
            <p>
              LeadingNG publishes the App. For privacy questions, data requests,
              or this policy, contact us at{" "}
              <a href="mailto:visualmotivator@leadingng.com">
                visualmotivator@leadingng.com
              </a>
              . We do not process your data on our systems (it stays on your
              device), but we are happy to answer questions or help you with
              deletion steps.
            </p>

            <p className="privacy-back">
              <Link to="/app/visual-motivator">← Visual Motivator</Link>
              <span className="privacy-back-sep"> · </span>
              <Link to="/app">Our apps</Link>
            </p>
          </div>
        </div>
      </section>

      <style>{`
        .page-hero-privacy {
          position: relative;
          padding: 88px 20px 64px;
          overflow: hidden;
          color: #fff;
        }
        .page-hero-privacy .hero-bg {
          position: absolute;
          inset: 0;
          background: linear-gradient(160deg, var(--color-bg-dark) 0%, #1a252f 100%);
          z-index: 0;
        }
        .page-hero-privacy > .container {
          position: relative;
          z-index: 1;
        }
        .page-hero-privacy .page-hero-label {
          display: block;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--color-primary);
          margin-bottom: 8px;
        }
        .page-hero-privacy .page-title {
          margin: 0 0 12px;
          font-size: clamp(1.75rem, 4vw, 2.25rem);
          font-weight: 700;
          letter-spacing: -0.02em;
        }
        .page-hero-privacy .page-intro {
          margin: 0;
          opacity: 0.92;
          font-size: 1.05rem;
        }
        .page-intro-muted { opacity: 0.85; font-size: 0.95rem; }
        .scroll-progress-bar {
          position: fixed;
          top: 0;
          left: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
          z-index: 9999;
          transition: width 0.1s ease-out;
          box-shadow: 0 0 10px var(--color-primary);
        }
        .privacy-section {
          background: linear-gradient(180deg, #f5f6f8 0%, #e8eaed 100%);
        }
        .privacy-content { max-width: 720px; margin: 0 auto; }
        .privacy-intro { font-size: 1.05rem; margin-bottom: 0.75rem; }
        .privacy-local-only { font-size: 1.05rem; margin-bottom: 2rem; padding: 1rem 1.15rem; background: rgba(0,0,0,0.05); border-left: 4px solid var(--color-primary); border-radius: 0 6px 6px 0; }
        .privacy-content h2 {
          font-size: 1.25rem;
          font-weight: 700;
          margin: 2rem 0 0.75rem;
          color: var(--color-text, #1a1a1a);
        }
        .privacy-content h2:first-of-type { margin-top: 0; }
        .privacy-content p { margin: 0 0 1rem; line-height: 1.6; }
        .privacy-content ul { margin: 0 0 1rem; padding-left: 1.5rem; }
        .privacy-content li { margin-bottom: 0.35rem; }
        .privacy-content a { color: var(--color-primary); text-decoration: underline; }
        .privacy-content a:hover { text-decoration: none; }
        .privacy-back { margin-top: 2.5rem; padding-top: 1.5rem; border-top: 1px solid rgba(0,0,0,0.08); }
        .privacy-back a { text-decoration: none; font-weight: 500; }
        .privacy-back a:hover { text-decoration: underline; }
        .privacy-back-sep { color: rgba(0,0,0,0.35); margin: 0 0.25rem; }
      `}</style>
    </>
  );
}
